import React, { ReactElement, ReactNode } from 'react';
import {
  Box,
  Button,
  Heading,
  HStack,
  isMobile,
  Label,
  Link,
  Text,
  useToast,
  VStack,
} from '@medely/ui-kit';
import { FixedFooterLayout } from '@mp/components/FixedFooterLayout';
import { WorkforceUnlockShiftsImage } from '@mp/components/images';
import { useProNavigate } from '@mp/contexts';
import { useProRouteParams } from '@mp/hooks/useProRouteParams';
import { useLocation, usePool, usePoolOptOutMutation, usePositions } from '@mp/hooks';
import { usePageTitle } from '@mp/components/pageLayout/TitleProvider';

const LEARN_MORE_URL = 'https://help.medely.com/en/articles/10270319-what-is-a-professional-pool';
const OPT_OUT_SUCCESS = "You won't see jobs from this pool anymore.";
const WHATS_NEXT_BULLETS = [
  'The facility will review your experience and qualifications.',
  "If confirmed as a match, you'll be invited to upload required credentials.",
  "Then you'll be able to book shifts.",
];

const IS_MOBILE = isMobile();

export type PoolInterestSubmittedScreenBaseProps = {
  header?: ReactNode;
  onDone: () => void;
};

export const PoolInterestSubmittedScreenBase = ({
  header,
  onDone,
}: PoolInterestSubmittedScreenBaseProps): ReactElement => {
  const { poolId } = useProRouteParams<'PoolInterestSubmitted'>('PoolInterestSubmitted').params;

  usePageTitle('Unlock shifts');

  const { locationId, positionIds } = usePool(poolId);
  const { location } = useLocation(locationId ?? 0);
  const { positions } = usePositions({
    positionIds: positionIds?.split(',').map(Number) ?? [],
  });

  const facilityName = location?.name;
  const positionsString =
    positions
      .map((position) => position.display_name)
      .filter(Boolean)
      .join(', ') || null;

  const { optOut, isOptingOut } = usePoolOptOutMutation(poolId);
  const toast = useToast();
  const { navigate } = useProNavigate();

  const handleOptOut = async () => {
    try {
      await optOut();
      toast.success(OPT_OUT_SUCCESS);
      navigate('FindJobs');
    } catch {
      // usePoolOptOutMutation surfaces the error toast; staying on this screen is intended.
    }
  };

  return (
    <FixedFooterLayout
      height="98%"
      footerContent={
        <HStack gap={2} ml={IS_MOBILE ? 1.5 : 0} width={IS_MOBILE ? '95%' : '100%'}>
          <Box flex={1}>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleOptOut}
              disabled={isOptingOut}
              loading={isOptingOut}
              testId="pool-interest-submitted-opt-out"
            >
              Not interested
            </Button>
          </Box>
          <Box flex={1}>
            <Button
              fullWidth
              onClick={onDone}
              disabled={isOptingOut}
              testId="pool-interest-submitted-done"
            >
              Done
            </Button>
          </Box>
        </HStack>
      }
    >
      {header}
      <VStack alignItems="center" width="100%">
        <VStack alignItems="center" maxWidth="343px">
          <Box mb={2}>
            <WorkforceUnlockShiftsImage />
          </Box>
          <VStack alignItems="center" mb={4}>
            <Heading size="s">Interest shared with facility</Heading>
            {facilityName ? <Text size="l">{facilityName}</Text> : null}
            {positionsString ? <Label size="m">{positionsString}</Label> : null}
          </VStack>
          <Box width="100%" px={2} pt={2} pb={1.5} bgColor="surface.secondary" borderRadius="8px">
            <Box mb={1.5}>
              <Label size="m">What&apos;s next</Label>
            </Box>
            <VStack gap={1.5}>
              {WHATS_NEXT_BULLETS.map((bullet) => (
                <Text key={bullet} size="m">{`• ${bullet}`}</Text>
              ))}
            </VStack>
          </Box>
          <HStack mt={5}>
            <Box pr={0.5}>
              <Text size="m">Have questions?</Text>
            </Box>
            <Link variant="text" size="m" url={LEARN_MORE_URL}>
              Learn more
            </Link>
            <Text size="m">.</Text>
          </HStack>
        </VStack>
      </VStack>
    </FixedFooterLayout>
  );
};
